// Phase 4 — the offline decrypt + entitlement gate for rented audiobooks.
//
// This is the pure, browser-side counterpart to the three earlier phases. It has
// no Capacitor, no network and no storage dependency, so it is unit-testable on
// its own and is the single place where the security decision lives:
//
//   NOTHING decrypts unless the signed entitlement token verifies against the
//   bundled Phase 2 public key AND we are still inside expiresAt. Fail closed.
//
// Two halves:
//   - checkOfflineEntitlement: verifies the Phase 2 token offline (Web Crypto +
//     the bundled public key) and enforces the baked-in expiry. The expiry is the
//     rental's own expiry (pinned server-side in Phase 3), so offline access can
//     never outlive the rental as long as we refuse past it.
//   - decryptChapterData: decrypts one chapter's ciphertext in memory, matching
//     Phase 1 byte for byte — AES-256-GCM, 12-byte IV, with the 16-byte auth tag
//     appended to the ciphertext (the layout Web Crypto's single-call decrypt
//     expects). The returned plaintext is transient and must never be written to
//     disk.
//
// The content key and the token are never logged from here.

import { verifyToken } from "./rentalVerify";
import { RENTAL_SIGNING_PUBLIC_KEY_PEM } from "./rentalSigningPublicKey";

// Decode a standard base64 string (the content key, IVs and ciphertext all travel
// as plain base64, not base64url) to a Uint8Array via the browser's atob.
function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// Resolve a Web Crypto SubtleCrypto. In the app this is the global crypto.subtle;
// tests inject one (jsdom does not provide crypto.subtle).
function resolveSubtle(injected) {
  if (injected) return injected;
  if (typeof window !== "undefined" && window.crypto && window.crypto.subtle) {
    return window.crypto.subtle;
  }
  throw new Error("Web Crypto (crypto.subtle) is not available in this environment");
}

// Verify the offline entitlement, fully offline. Returns
//   { ok: true, expiresAt }                       when playback is permitted, or
//   { ok: false, reason: 'invalid' | 'mismatch' | 'expired', expired? }  otherwise.
// `reason`:
//   - 'invalid'  the signature did not verify against the bundled public key
//                (forged, tampered, or no token at all), or a binding input
//                (uid / audiobookId) was missing,
//   - 'mismatch' the token is for a different reader or a different audiobook,
//   - 'expired'  the signature is good but expiresAt is now or in the past.
// The token is bound to BOTH the reader (uid) and the book (audiobookId), both of
// which Phase 3 signs into the payload. uid and audiobookId are required: an
// absent one fails closed ('invalid') rather than silently skipping the check, so
// one reader's token can never be replayed for another reader or book.
// publicKeyPem defaults to the bundled Phase 2 key; tests inject a throwaway key.
export async function checkOfflineEntitlement({
  token, uid, audiobookId, now, subtle, publicKeyPem,
} = {}) {
  const pem = publicKeyPem || RENTAL_SIGNING_PUBLIC_KEY_PEM;

  const { valid, payload } = await verifyToken(token, pem, { subtle });
  if (!valid || !payload) return { ok: false, reason: "invalid" };

  // Both binding inputs are mandatory. A missing uid or audiobookId must fail
  // closed, never disable the binding.
  if (!uid || !audiobookId) return { ok: false, reason: "invalid" };

  // Bind to this exact reader: a token minted for one uid cannot unlock playback
  // for another.
  if (payload.uid !== uid) return { ok: false, reason: "mismatch" };

  // Bind to this exact audiobook: one rental's token cannot unlock another book's
  // ciphertext.
  if (payload.audiobookId !== audiobookId) return { ok: false, reason: "mismatch" };

  // The signed expiry is authoritative. A non-numeric or past expiry fails closed.
  const ts = typeof now === "number" ? now : Date.now();
  if (typeof payload.expiresAt !== "number" || payload.expiresAt <= ts) {
    return { ok: false, reason: "expired", expired: true };
  }

  return { ok: true, expiresAt: payload.expiresAt };
}

// Decrypt one chapter's ciphertext in memory and return the plaintext bytes.
//   data       base64 ciphertext WITH the 16-byte GCM auth tag appended (Phase 1
//              layout), exactly as the .enc object holds it,
//   iv         base64 12-byte GCM nonce for this chapter,
//   contentKey base64 AES-256 key from the bundle.
// Matches functions/lib/audioEncryption.js byte for byte: AES-256-GCM, single-call
// Web Crypto decrypt over ciphertext||tag. Throws if authentication fails (wrong
// key, wrong IV, or tampered ciphertext). The returned bytes are transient — feed
// them straight to the player and never persist them.
export async function decryptChapterData({ data, iv, contentKey, subtle } = {}) {
  if (!data || !iv || !contentKey) {
    throw new Error("decryptChapterData requires data, iv and contentKey");
  }
  const s = resolveSubtle(subtle);

  const key = await s.importKey(
    "raw",
    base64ToBytes(contentKey),
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );

  const plain = await s.decrypt(
    { name: "AES-GCM", iv: base64ToBytes(iv) },
    key,
    base64ToBytes(data),
  );
  return new Uint8Array(plain);
}

// Structural backstop: decrypt a chapter ONLY when the entitlement gate passes.
// This binds decryption to verification in one place, so raw decryptChapterData
// can never run without a passing gate even if a future caller forgets to check.
// Runs checkOfflineEntitlement first (same uid + audiobookId + expiry + signature
// rules) and throws on failure, carrying the reason on err.reason so the caller
// can branch on 'expired' vs 'invalid' / 'mismatch' if it wants to. Returns the
// plaintext bytes only when entitled.
export async function decryptChapterIfEntitled({
  token, uid, audiobookId, data, iv, contentKey, now, subtle, publicKeyPem,
} = {}) {
  const ent = await checkOfflineEntitlement({
    token, uid, audiobookId, now, subtle, publicKeyPem,
  });
  if (!ent.ok) {
    const err = new Error(`Offline entitlement check failed: ${ent.reason}`);
    err.reason = ent.reason;
    throw err;
  }
  return decryptChapterData({ data, iv, contentKey, subtle });
}
